import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import {
  Phone,
  Building2,
  HeartPulse,
  CreditCard,
  AlertCircle,
} from "lucide-react";
const AcceleratedTmsCoverage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "Insurance Coverage for Accelerated TMS & SAINT® | Neuro Wellness TMS Centers of America"
        }
        description={
          "Insurance coverage for accelerated TMS is changing. Learn how commercial plans, Medicare, and Medicare Advantage handle SAINT — and how we verify your benefits. Coral Springs, FL."
        }
        canonical={"/accelerated-tms-coverage"}
        keywords={
          "accelerated TMS insurance coverage, SAINT Medicare coverage, Category III CPT codes TMS, TMS prior authorization, Medicare Part B TMS"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "Insurance Coverage for Accelerated TMS",
          description:
            "A guide to insurance coverage for accelerated TMS protocols including commercial, Medicare, and Medicare Advantage.",
          about: [
            "TMS Insurance Coverage",
            "Medicare",
            "Category III CPT Codes",
            "Prior Authorization",
          ],
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              Patient's Guide · Part 3
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              What Insurance Coverage Looks Like
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              Coverage for accelerated TMS is changing quickly. Here's what to
              expect — and how we help you navigate it.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-transparent px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
              >
                <Phone className="h-5 w-5" />
                Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            {/* Commercial — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <Building2 className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  Commercial insurance
                </h2>
              </div>
              <p>
                This varies a lot and changes quickly. As recently as 2025, many
                major insurers classified accelerated TMS as experimental —
                meaning no payment. By mid-2026, coverage had expanded
                significantly. Both can be true at the same time, because
                coverage is decided plan by plan.
              </p>
              <p>Here's what to do:</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Ask your insurer for their medical policy on TMS. Read the
                    section on accelerated protocols.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Get prior authorization in writing before you travel or pay
                    anything.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Check whether the clinic is in your network — a covered
                    service at an out-of-network clinic can still leave you
                    paying most of the bill.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Ask what happens if you're denied partway through treatment.
                  </span>
                </li>
              </ul>
            </div>

            {/* Medicare — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <HeartPulse className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  Original Medicare
                </h2>
              </div>
              <p>
                Medicare Part B covers TMS for severe depression when it's
                medically necessary and documented. Medicare doesn't cover TMS
                for other conditions — only depression.
              </p>
              <p>
                In 2026, after the Part B deductible ($283), you're responsible
                for 20% of the approved amount. There's no flat per-session
                price — your cost depends on what Medicare allows for each
                service.
              </p>
              <p>
                SAINT uses newer billing codes that are still being adopted.
                Some Medicare contractors handle these on a case-by-case basis.
                Where you're treated also matters — hospital outpatient
                departments and freestanding offices may be paid differently.
              </p>
            </div>

            {/* Medicare Advantage — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <HeartPulse className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  Medicare Advantage
                </h2>
              </div>
              <p>
                Medicare Advantage plans must cover at least what Original
                Medicare covers, but they add their own networks and
                prior-authorization rules. In practice: prior auth is usually
                required, you need to stay in network, and traveling out of
                state for treatment is the most common point of failure. If
                you're considering traveling, ask your plan about out-of-area
                care before you commit.
              </p>
            </div>

            {/* Deposits — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <CreditCard className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  If a clinic asks for a deposit
                </h2>
              </div>
              <p>
                Some clinics ask for a deposit up front, bill your insurance,
                and refund you when payment arrives. This can be fine — and it
                can leave you exposed. Before you pay anything:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Get the refund terms in writing — full amount, timeline, and
                    what triggers it.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Ask what happens if the claim is denied. Do you owe the
                    balance?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    If you're on Medicare, ask whether the provider participates
                    or has opted out. Rules on what they can collect up front
                    are strict.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    Ask whether you'll be asked to sign an Advance Beneficiary
                    Notice — it shifts financial responsibility to you.
                  </span>
                </li>
              </ul>
            </div>

            {/* Out of pocket — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  What "out of pocket" really includes
                </h2>
              </div>
              <p>
                Quoted prices often leave things out. Ask whether the number
                covers:
              </p>
              <ul className="space-y-3">
                {[
                  "The initial consultation and psychiatric evaluation",
                  "The fMRI scan (often billed separately by an imaging facility)",
                  "All fifty treatment sessions",
                  "Follow-up visits after treatment",
                  "Maintenance or retreatment if symptoms return",
                  "Travel, lodging, meals, and time off work",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                That last item isn't small. A treatment week that requires
                flights and six nights of lodging can add meaningfully to a
                quoted price.
              </p>
            </div>

            {/* How we help */}
            <div className="rounded-2xl border-2 border-accent/30 bg-accent/5 p-6 sm:p-8">
              <h2 className="font-headings text-xl font-bold text-primary">
                How we help with coverage
              </h2>
              <p className="mt-2 text-[17px] leading-relaxed text-muted-foreground">
                We verify your benefits before you commit to anything. Our team
                contacts your insurer, confirms what's covered, checks prior
                authorization requirements, and gives you a written summary of
                your costs — all before your first session. If an authorization
                is needed, we submit it and handle appeals if it's denied.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/#consultation"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
                >
                  Schedule a Free Phone Consultation
                </a>
                <a
                  href="tel:9548279793"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary/30 bg-transparent px-6 py-3 text-base font-semibold text-primary hover:bg-primary/5"
                >
                  <Phone className="h-5 w-5" />
                  Call 954-827-9793
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-light-blue bg-light-blue/30 p-6">
              <h3 className="font-headings text-lg font-bold text-primary">
                Continue reading
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/accelerated-tms-five-day-week"
                  className="text-accent hover:underline"
                >
                  Part 4: What the Five-Day Week Is Like →
                </Link>
                <Link
                  to="/accelerated-tms-other-conditions"
                  className="text-accent hover:underline"
                >
                  Part 5: Can TMS Help With Other Conditions? →
                </Link>
                <Link
                  to="/how-insurance-works"
                  className="text-accent hover:underline"
                >
                  How Insurance Verification Works →
                </Link>
                <Link
                  to="/does-federal-bcbs-cover-saint"
                  className="text-accent hover:underline"
                >
                  Does Federal BCBS Cover SAINT? →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Related Resources"
          links={[...allTreatmentsLinks, ...allDecisionLinks]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default AcceleratedTmsCoverage;
